// The Dome, Events page (W4). Plan graphic, bracket, pinned steps, working inquiry form.
import { mountChrome } from "../shared/chrome";
import { motionOn, gsap, ScrollTrigger, startLenis } from "../shared/motion";

/* Line split for mask reveals. Local copy of the shared helper: the shared one starts its line
   tracker at -1, and an inline box at offsetTop -1 (light display face, tight leading) then crashes. */
function splitLines(el: HTMLElement): HTMLElement[] {
  const words = (el.textContent ?? "").trim().split(/\s+/);
  el.textContent = "";
  const spans = words.map((w, i) => {
    const s = document.createElement("span");
    s.textContent = w + (i < words.length - 1 ? " " : "");
    el.appendChild(s);
    return s;
  });
  const lines: HTMLSpanElement[][] = [];
  let top = Number.NEGATIVE_INFINITY;
  for (const s of spans) {
    const t = s.offsetTop;
    if (Math.abs(t - top) > 2) { lines.push([]); top = t; }
    lines[lines.length - 1].push(s);
  }
  el.textContent = "";
  return lines.map((ws) => {
    const mask = document.createElement("span");
    mask.style.cssText = "display:block;overflow:hidden";
    const inner = document.createElement("span");
    inner.style.display = "block";
    inner.textContent = ws.map((w) => w.textContent).join("");
    mask.appendChild(inner);
    el.appendChild(mask);
    return inner;
  });
}
function revealHeadings() {
  document.querySelectorAll<HTMLElement>("[data-reveal]").forEach((el) => {
    const label = el.textContent ?? "";
    const lines = splitLines(el);
    lines.forEach((l) => l.setAttribute("aria-hidden", "true"));
    el.setAttribute("aria-label", label);
    gsap.from(lines, {
      yPercent: 105, duration: 0.6, ease: "power4.out", stagger: 0.08,
      scrollTrigger: { trigger: el, start: "top 85%", once: true },
      onComplete: () => { el.textContent = label; el.removeAttribute("aria-label"); },
    });
  });
}
import { Flip } from "gsap/Flip";
import { createBracket, type BracketState } from "./canvas";
import { CourtPlan } from "../shared/plan";

mountChrome({ active: "events" });

const MOTION = motionOn();
document.documentElement.classList.toggle("motion", MOTION);
document.documentElement.classList.toggle("calm", !MOTION);

function syncOffsets() {
  const root = document.documentElement;
  root.style.setProperty("--hdr", headerOffset() + "px");
  const sticky = document.querySelector<HTMLElement>(".open-sticky");
  if (!sticky || !MOTION) return;
  const tab = parseFloat(getComputedStyle(root).getPropertyValue("--tab")) || 0;
  const avail = innerHeight - tab - headerOffset();
  const h = sticky.offsetHeight;
  // centre the held composition in the view; if it is taller, hold it by its bottom edge
  const top = h <= avail ? headerOffset() + (avail - h) / 2 : innerHeight - tab - h;
  root.style.setProperty("--open-top", top + "px");
}
syncOffsets();
addEventListener("resize", syncOffsets);

const SVG = "http://www.w3.org/2000/svg";
const svgEl = <K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number>) => {
  const el = document.createElementNS(SVG, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  return el;
};

/* Real club media (prototype only, Brian-approved), served from /media. Captions in the HTML describe each frame. */
function setMedia(id: string, file: string, alt: string, pos = "50% 50%") {
  const img = document.getElementById(id) as HTMLImageElement | null;
  if (!img) return;
  img.src = `/media/${file}`;
  img.alt = alt;
  img.style.objectPosition = pos;
}
setMedia("ph-company", "classic-3.jpg", "A large group of players in matching black shirts lines up at the net on a blue court", "50% 72%");
setMedia("ph-tour", "details-3.jpg", "The black MejorSet court frame where the Court Blue playing surface meets the green run-off turf", "50% 55%");
setMedia("ph-brand", "lighting-3-c.jpg", "The hall under the white membrane: curved floodlight poles over blue courts, benches and The Dome banners", "50% 64%");
setMedia("ph-clinic", "details-1.jpg", "The white court frame on Court Blue turf, the DŌME mark engraved in its corner", "50% 42%");
setMedia("ph-inq", "airdome-dome.jpg", "The white air dome with The Dome mark among the trees of Permata Hijau, seen from a drone", "40% 50%");
setMedia("ph-step3", "classic-4-c.jpg", "Players celebrate at the net during a Padel Classic at The Dome", "56% 50%");
setMedia("ph-kids", "framed-2.jpg", "The hall under the lit membrane: blue courts, white benches and curved floodlight poles", "50% 70%");

/* 1. Dome plan: the real building from above, a rounded air dome with 8 courts in two rows of four */
const heroPlan = new CourtPlan(document.querySelector<HTMLCanvasElement>(".plan-cv")!, { motion: MOTION });
const plan = heroPlan.state;
const drawPlan = heroPlan.queue;
plan.light.fill(1);
plan.mezz = 1;
// the plan sizes its own height: re-centre the held opening whenever its height settles
new ResizeObserver(() => syncOffsets()).observe(document.querySelector<HTMLElement>(".open-sticky")!);

const STEPS = [2, 4, 8, 8]; // courts lit per group size step
const sizeRows = [...document.querySelectorAll<HTMLElement>(".size")];
let curStep = -1;
function setStep(step: number, animate: boolean) {
  if (step === curStep) return;
  const prev = STEPS[curStep] ?? 0;
  curStep = step;
  sizeRows.forEach((r, i) => r.classList.toggle("is-on", i === step));
  moveIndicator(step, animate);
  const n = STEPS[step] ?? 0;
  const whole = step === 3 ? 1 : 0;
  if (!animate) {
    plan.light.forEach((_, i, a) => (a[i] = i < n ? 1 : 0));
    plan.mezz = whole;
    drawPlan();
    return;
  }
  // light new courts one after another
  plan.light.forEach((_, i) => {
    const on = i < n;
    gsap.to(plan.light, { [i]: on ? 1 : 0, duration: on ? 0.5 : 0.3, delay: on ? Math.max(0, i - prev) * 0.09 : 0, ease: "power3.out", overwrite: "auto", onUpdate: drawPlan });
  });
  gsap.to(plan, { mezz: whole, duration: 0.6, ease: "power3.out", overwrite: "auto", onUpdate: drawPlan });
}

/* The rail's selected fill: one ivory tile that slides between sizes (GSAP Flip, transform only) */
let indicator: HTMLElement | null = null;
function moveIndicator(step: number, animate: boolean) {
  if (!MOTION) return;
  const target = sizeRows[step];
  if (!target) return;
  if (!indicator) {
    indicator = document.createElement("span");
    indicator.className = "size-ind";
    indicator.setAttribute("aria-hidden", "true");
    target.prepend(indicator);
    return;
  }
  if (indicator.parentElement === target) return;
  const state = Flip.getState(indicator);
  target.prepend(indicator);
  if (animate) Flip.from(state, { duration: 0.55, ease: "power3.inOut", scale: true });
}

/* 2b. Bracket: 8 pairs, quarter finals to a champion. Example results, labelled as such. */
const rounds: string[][] = [
  ["Pair 1", "Pair 2", "Pair 3", "Pair 4", "Pair 5", "Pair 6", "Pair 7", "Pair 8"],
  ["Pair 1", "Pair 4", "Pair 5", "Pair 7"],
  ["Pair 1", "Pair 7"],
  ["Pair 7"],
];
const bracket: BracketState = { lines: [1, 1, 1], labels: [1, 1, 1, 1] };
const bracketCv = document.querySelector<HTMLCanvasElement>(".bracket")!;
const drawBracket = createBracket(bracketCv, rounds, matchMedia("(max-width: 639.98px)").matches, bracket);
document.fonts?.ready.then(() => drawBracket());

/* 3. Step 2 graphic: the same court map with an example allocation, fill steps only */
const allocPlan = new CourtPlan(document.querySelector<HTMLCanvasElement>(".alloc-cv")!, { motion: MOTION, base: "surface" });
allocPlan.state.light.splice(0, 8, 1, 1, 1, 1, 0, 0, 0, 0);
allocPlan.state.sel.splice(0, 8, 0, 0, 0, 0, 1, 1, 1, 1);
allocPlan.queue();

/* Anniversaries: the same plan, small, every court in play */
const capPlan = new CourtPlan(document.querySelector<HTMLCanvasElement>(".cap-cv")!, { motion: MOTION });
capPlan.state.light.fill(1);
capPlan.queue();
if (MOTION) {
  capPlan.state.light.fill(0);
  ScrollTrigger.create({ trigger: ".capacity", start: "top 85%", once: true, onEnter: () => capPlan.lights([1, 1, 1, 1, 1, 1, 1, 1], { order: [1, 2, 3, 4, 5, 6, 7, 8], stagger: 0.08 }) });
}

/* Clubs card: the current month, laid out Monday first, club night every Thursday, today marked */
function buildMonth() {
  const cal = document.querySelector<HTMLElement>(".cal");
  const label = document.getElementById("cal-m");
  if (!cal || !label) return;
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth(), today = now.getDate();
  const lead = (new Date(y, m, 1).getDay() + 6) % 7;
  const days = new Date(y, m + 1, 0).getDate();
  const month = now.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  label.textContent = month;
  const row = (cells: string) => `<div class="cal-row" role="row">${cells}</div>`;
  const heads = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => `<span role="columnheader" class="caption ink-subtle">${d}</span>`).join("");
  const cells: string[] = [];
  for (let i = 0; i < lead; i++) cells.push(`<span role="cell" class="cal-d is-blank"></span>`);
  for (let d = 1; d <= days; d++) {
    const dow = (lead + d - 1) % 7;
    const club = dow === 3, isToday = d === today;
    const cls = ["cal-d", "num", club ? "is-club" : "", isToday ? "is-today" : ""].filter(Boolean).join(" ");
    const name = `${d} ${month.split(" ")[0]}${club ? ", club night" : ""}${isToday ? ", today" : ""}`;
    cells.push(`<span role="cell" class="${cls}" aria-label="${name}">${d}</span>`);
  }
  while (cells.length % 7) cells.push(`<span role="cell" class="cal-d is-blank"></span>`);
  let html = row(heads);
  for (let i = 0; i < cells.length; i += 7) html += row(cells.slice(i, i + 7).join(""));
  cal.innerHTML = html;
}
buildMonth();

/* Clinic week: mark today */
{
  const row = document.querySelector<HTMLElement>(`.day[data-dow="${new Date().getDay()}"]`);
  if (row) {
    row.classList.add("is-today");
    row.setAttribute("aria-current", "date");
    const n = row.querySelector(".day-n");
    if (n) n.textContent = "Today";
  }
}

/* Motion */
if (MOTION) {
  gsap.registerPlugin(Flip);
  startLenis();
  const head = document.querySelector<HTMLElement>("[data-split]")!;
  const label = head.textContent ?? "";
  const lines = splitLines(head);
  head.setAttribute("aria-label", label);
  lines.forEach((l) => l.setAttribute("aria-hidden", "true"));
  revealHeadings();

  // Page load: the hall fades and settles in, courts light 1 to 8, the headline masks in, then the story starts at eight players
  plan.reveal = 0; plan.mezz = 0; plan.light.fill(0);
  drawPlan();
  let introDone = false;
  let wanted = 0;
  const intro = gsap.timeline({ delay: 0.15, onComplete: () => { introDone = true; curStep = -1; setStep(wanted, true); } });
  intro.from(lines, { yPercent: 105, duration: 0.9, ease: "power4.out", stagger: 0.1 }, 0)
    .to(plan, { reveal: 1, duration: 0.9, ease: "power3.out", onUpdate: drawPlan }, 0.1)
    .from([".open-lead", ".open-cta"], { opacity: 0, y: 16, duration: 0.7, ease: "power3.out", stagger: 0.08 }, 0.4)
    .from(".size", { opacity: 0, x: 24, duration: 0.6, ease: "power3.out", stagger: 0.07, clearProps: "opacity,transform" }, 0.6);
  plan.light.forEach((_, i) => intro.to(plan.light, { [i]: 1, duration: 0.35, ease: "power2.out", onUpdate: drawPlan }, 1.0 + i * 0.1));
  intro.to(plan, { mezz: 1, duration: 0.4, onUpdate: drawPlan }, 1.8).to({}, { duration: 0.35 });

  // Scroll: the rail and the courts follow 8, 16, 32 and the whole dome
  ScrollTrigger.create({
    trigger: ".open-track", start: "top top", end: "bottom bottom",
    onUpdate: (st) => { wanted = Math.min(3, Math.floor(st.progress * 4.0001)); if (introDone) setStep(wanted, true); },
  });
  setStep(0, false);
  plan.light.fill(0); plan.mezz = 0; drawPlan();

  // Formats: photos settle from 1.08 with a short inner parallax
  document.querySelectorAll<HTMLElement>("[data-parallax], [data-grow]").forEach((fig) => {
    const img = fig.querySelector(".clip img")!;
    gsap.fromTo(img, { scale: 1.14, yPercent: -1.5 }, { scale: 1.04, yPercent: 1.5, ease: "none", scrollTrigger: { trigger: fig, start: "top bottom", end: "bottom top", scrub: true } });
  });
  const grow = document.querySelector<HTMLElement>("[data-grow] .clip")!;
  gsap.fromTo(grow, { scale: 0.92 }, { scale: 1, ease: "none", scrollTrigger: { trigger: grow, start: "top bottom", end: "top 40%", scrub: true } });
  gsap.utils.toArray<HTMLElement>(".duo-card").forEach((el, i) => {
    gsap.from(el, { opacity: 0, y: 24, duration: 0.7, delay: i * 0.1, ease: "power3.out", scrollTrigger: { trigger: el, start: "top 96%", once: true } });
  });

  // Copy blocks rise in
  gsap.utils.toArray<HTMLElement>(".fmt-copy > p, .stat, .facts li, .bracket-card > p, .clinic-copy > p, .clinic-actions, .kids-item, .inq-side > p, .contact li").forEach((el) => {
    gsap.from(el, { opacity: 0, y: 16, duration: 0.6, ease: "power3.out", scrollTrigger: { trigger: el, start: "top 96%", once: true } });
  });

  // Bracket draws round by round as you scroll
  bracket.lines = [0, 0, 0];
  bracket.labels = [0, 0, 0, 0];
  drawBracket();
  const brTl = gsap.timeline({ scrollTrigger: { trigger: ".bracket-card", start: "top 95%", end: "top 35%", scrub: 0.5 }, onUpdate: drawBracket });
  brTl.to(bracket.labels, { 0: 1, duration: 0.25 });
  for (let r = 1; r < rounds.length; r++) {
    brTl.to(bracket.lines, { [r - 1]: 1, duration: 0.45, ease: "none" }).to(bracket.labels, { [r]: 1, duration: 0.2 });
  }

  // Rota fills cell by cell, round by round
  const cells = gsap.utils.toArray<HTMLElement>(".rota-m").sort((a, b) => Number(a.dataset.round) - Number(b.dataset.round));
  gsap.from(cells, { opacity: 0, scale: 0.85, duration: 0.4, ease: "power3.out", stagger: 0.06, scrollTrigger: { trigger: ".rota", start: "top 92%", once: true } });
  // Calendar: each Thursday fills in turn
  const clubs = gsap.utils.toArray<HTMLElement>(".cal-d.is-club");
  clubs.forEach((d) => { const f = document.createElement("span"); f.className = "fill"; f.setAttribute("aria-hidden", "true"); d.prepend(f); d.classList.add("has-fill"); });
  gsap.from(".cal-d.is-club .fill", { scale: 0, duration: 0.5, ease: "back.out(1.8)", stagger: 0.15, scrollTrigger: { trigger: ".cal", start: "top 92%", once: true } });

  // Bressel week: rows stagger in, then today's fill slides across
  gsap.from(".day", { opacity: 0, y: 20, duration: 0.6, ease: "power3.out", stagger: 0.06, scrollTrigger: { trigger: ".week", start: "top 90%", once: true } });
  const todayRow = document.querySelector<HTMLElement>(".day.is-today");
  if (todayRow) {
    todayRow.classList.remove("is-today");
    todayRow.classList.add("today-pending");
    const f = document.createElement("span"); f.className = "fill"; f.setAttribute("aria-hidden", "true"); todayRow.prepend(f);
    gsap.fromTo(f, { scaleX: 0 }, { scaleX: 1, duration: 0.7, ease: "power3.inOut", delay: 0.5, scrollTrigger: { trigger: ".week", start: "top 90%", once: true },
      onStart: () => todayRow.classList.add("is-today") });
  }
  gsap.from(".tag", { opacity: 0, y: 6, duration: 0.4, ease: "power3.out", stagger: 0.05, scrollTrigger: { trigger: ".week", start: "top 90%", once: true } });

  // How it works: pinned; the film grows, steps slide, the progress line tracks the step
  const mm = gsap.matchMedia();
  gsap.from(".how-head", { opacity: 0, y: 16, duration: 0.6, ease: "power3.out", scrollTrigger: { trigger: ".how", start: "top 95%", once: true } });
  mm.add("(min-width: 1024px)", () => {
    const track = document.querySelector<HTMLElement>(".how-track")!;
    const count = document.querySelector<HTMLElement>(".how-count")!;
    const bar = document.querySelector<HTMLElement>(".how-bar")!;
    const pb = document.querySelector<HTMLElement>(".how-track-bar")!;
    const clip = document.querySelector<HTMLElement>(".how-clip")!;
    const dist = () => Math.max(0, track.scrollWidth - clip.clientWidth);
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: ".how-pin", pin: ".how-pin", start: () => "center " + Math.round((innerHeight + headerOffset()) / 2) + "px", end: () => "+=" + dist() * 1.3,
        scrub: 0.6, invalidateOnRefresh: true, anticipatePin: 1,
        onUpdate: (st) => {
          const n = Math.min(3, 1 + Math.floor(st.progress * 3));
          count.textContent = `Step ${n} of 3`;
          pb.setAttribute("aria-valuenow", String(n));
        },
      },
    });
    tl.fromTo(".film-frame", { scale: 0.86 }, { scale: 1, duration: 0.25, ease: "power2.out" }, 0)
      .to(track, { x: () => -dist(), duration: 1, ease: "none" }, 0.15)
      .fromTo(bar, { scaleX: 1 / 3 }, { scaleX: 1, duration: 1, ease: "none" }, 0.15);
  });
  mm.add("(max-width: 1023.98px)", () => {
    gsap.utils.toArray<HTMLElement>(".step").forEach((el) => {
      gsap.from(el, { opacity: 0, y: 24, duration: 0.7, ease: "power3.out", scrollTrigger: { trigger: el, start: "top 95%", once: true } });
    });
  });

  // Inquiry: the card rises, then its fields stagger in
  gsap.from(".inq-card", { opacity: 0, y: 24, duration: 0.8, ease: "power3.out", scrollTrigger: { trigger: ".inq", start: "top 95%", once: true } });
  gsap.from("#inq-form > *", { opacity: 0, y: 14, duration: 0.5, ease: "power3.out", stagger: 0.05, delay: 0.2, clearProps: "opacity,transform", scrollTrigger: { trigger: ".inq", start: "top 90%", once: true } });
  window.addEventListener("load", () => { syncOffsets(); ScrollTrigger.refresh(); });
} else {
  setStep(3, false);
  // calm: no frame loop at all (ScrollTrigger and the ticker stay asleep)
  const rest = () => { ScrollTrigger.getAll().forEach((t) => t.kill()); ScrollTrigger.disable(); gsap.ticker.sleep(); };
  rest();
  addEventListener("load", () => setTimeout(rest, 0));
}

/* Step 3 film: lights on under the membrane; plays only with motion on, poster otherwise */
const film = document.querySelector<HTMLVideoElement>(".step-film");
if (film) {
  film.poster = "/media/loop-lights.jpg";
  if (MOTION) {
    film.src = "/media/loop-lights.mp4";
    film.muted = true;
    new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) film.play().catch(() => {}); else film.pause(); }), { threshold: 0.25 }).observe(film);
  } else film.removeAttribute("autoplay");
}


/* Clinic: ask through the inquiry form, prefilled */
document.getElementById("clinic-cta")!.addEventListener("click", () => {
  pick("type", "clinic");
  pick("groupSize", "1-7");
  show("groupSize", "");
  const msg = form.querySelector<HTMLTextAreaElement>("#f-message")!;
  if (!msg.value.trim()) msg.value = "Bressel Academy clinic, first time on court. ";
  show("type", "");
});

function headerOffset(): number {
  const h = document.querySelector<HTMLElement>("#chrome-header header, #chrome-header > *");
  if (!h) return 0;
  const pos = getComputedStyle(h).position;
  return pos === "fixed" || pos === "sticky" ? h.offsetHeight : 0;
}

/* 4. Inquiry form */
const form = document.getElementById("inq-form") as HTMLFormElement;
const statusEl = document.getElementById("form-status")!;
const submitBtn = form.querySelector<HTMLButtonElement>(".submit")!;
const submitLabel = form.querySelector<HTMLElement>(".submit-label")!;
const done = document.getElementById("done")!;
const dateIn = document.getElementById("f-date") as HTMLInputElement;

const pad = (n: number) => String(n).padStart(2, "0");
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const today = new Date();
const maxD = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 14);
dateIn.min = iso(today);
dateIn.max = iso(maxD);

const PHONE = /^(\+62|0)8\d{7,12}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const normPhone = (v: string) => v.replace(/[\s\-().]/g, "");

type Field = "name" | "company" | "email" | "phone" | "date" | "groupSize" | "type" | "message";
const val = (n: string) => {
  const el = form.elements.namedItem(n);
  if (el instanceof RadioNodeList) return el.value;
  return (el as HTMLInputElement | null)?.value.trim() ?? "";
};

function check(f: Field): string {
  const v = val(f);
  switch (f) {
    case "name": return v.length < 2 ? "Enter your name." : "";
    case "email": return !v ? "Enter your email." : EMAIL.test(v) ? "" : "Check the email, it looks incomplete.";
    case "phone": return !v ? "Enter a phone number." : PHONE.test(normPhone(v)) ? "" : "Use a mobile number starting with +62 8 or 08.";
    case "date": {
      if (!v) return "";
      if (v < dateIn.min) return "Pick today or a later date.";
      if (v > dateIn.max) return "Pick a date within the next 14 days.";
      return "";
    }
    case "groupSize": return v ? "" : "Pick a group size.";
    case "type": return v ? "" : "Pick the type of event.";
    default: return "";
  }
}

function show(f: Field, msg: string) {
  const help = document.getElementById(`h-${f}`);
  const input = form.querySelector<HTMLElement>(`[name="${f}"]`);
  if (!help) return;
  const def = help.dataset.default ?? "";
  help.textContent = msg || def;
  help.classList.toggle("is-error", !!msg);
  help.classList.toggle("ink-subtle", !msg);
  if (msg) help.prepend(alertIcon());
  const group = input?.closest(".field");
  group?.classList.toggle("has-error", !!msg);
  form.querySelectorAll<HTMLElement>(`[name="${f}"]:not([type="hidden"]), .chips[data-name="${f}"]`).forEach((i) => i.setAttribute("aria-invalid", msg ? "true" : "false"));
}

function alertIcon(): SVGSVGElement {
  const s = svgEl("svg", { viewBox: "0 0 24 24", width: 16, height: 16, "aria-hidden": "true", class: "ico" }) as SVGSVGElement;
  s.append(svgEl("circle", { cx: 12, cy: 12, r: 9 }), svgEl("path", { d: "M12 7.5v5.5M12 16.2v.3" }));
  return s;
}

/* Chip radiogroups: buttons with role radio, roving tabindex, arrow keys; value in a hidden input */
function pick(name: string, value: string, focus = false) {
  const group = form.querySelector<HTMLElement>(`.chips[data-name="${name}"]`);
  const hidden = form.querySelector<HTMLInputElement>(`input[type="hidden"][name="${name}"]`);
  if (!group || !hidden) return;
  hidden.value = value;
  group.querySelectorAll<HTMLButtonElement>(".chip").forEach((b) => {
    const on = b.dataset.value === value;
    b.setAttribute("aria-checked", String(on));
    b.tabIndex = on ? 0 : -1;
    if (on && focus) b.focus();
  });
}
function resetChips() {
  form.querySelectorAll<HTMLElement>(".chips").forEach((g) => {
    g.querySelectorAll<HTMLButtonElement>(".chip").forEach((b, i) => { b.setAttribute("aria-checked", "false"); b.tabIndex = i === 0 ? 0 : -1; });
  });
}
form.querySelectorAll<HTMLElement>(".chips").forEach((group) => {
  const name = group.dataset.name as Field;
  const chips = [...group.querySelectorAll<HTMLButtonElement>(".chip")];
  chips.forEach((b, i) => {
    b.addEventListener("click", () => { pick(name, b.dataset.value!); touched.add(name); show(name, check(name)); });
    b.addEventListener("keydown", (e) => {
      const d = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
      if (!d) return;
      e.preventDefault();
      const next = chips[(i + d + chips.length) % chips.length];
      pick(name, next.dataset.value!, true);
      touched.add(name); show(name, check(name));
    });
  });
});

const FIELDS: Field[] = ["name", "company", "email", "phone", "date", "groupSize", "type", "message"];
const touched = new Set<Field>();
FIELDS.forEach((f) => {
  form.querySelectorAll<HTMLInputElement>(`[name="${f}"]`).forEach((el) => {
    el.addEventListener("blur", () => { if (el.value.trim() || touched.has(f)) { touched.add(f); show(f, check(f)); } });
    el.addEventListener("input", () => { if (touched.has(f)) show(f, check(f)); });
    el.addEventListener("change", () => { touched.add(f); show(f, check(f)); });
  });
});

function setStatus(msg: string | null, retry = false) {
  statusEl.replaceChildren();
  if (!msg) { statusEl.hidden = true; return; }
  statusEl.hidden = false;
  const p = document.createElement("p");
  p.className = "body";
  p.append(alertIcon(), document.createTextNode(msg));
  statusEl.append(p);
  if (retry) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "btn btn-secondary";
    b.textContent = "Try again";
    b.addEventListener("click", () => form.requestSubmit());
    statusEl.append(b);
  }
}

async function post(body: unknown, attempt = 0): Promise<Response> {
  try {
    const res = await fetch("/api/inquiries", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
    if ((res.status >= 500 || res.status === 404) && attempt < 2) {
      await new Promise((r) => setTimeout(r, 700 * (attempt + 1)));
      return post(body, attempt + 1);
    }
    return res;
  } catch (e) {
    if (attempt < 2) {
      await new Promise((r) => setTimeout(r, 700 * (attempt + 1)));
      return post(body, attempt + 1);
    }
    throw e;
  }
}

let sending = false;
form.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (sending) return;
  setStatus(null);
  let first: HTMLElement | null = null;
  for (const f of FIELDS) {
    touched.add(f);
    const msg = check(f);
    show(f, msg);
    if (msg && !first) first = form.querySelector<HTMLElement>(`.chips[data-name="${f}"] .chip, [name="${f}"]:not([type="hidden"])`);
  }
  if (first) { first.focus(); return; }

  const body: Record<string, string> = {
    name: val("name"), email: val("email"), phone: normPhone(val("phone")),
    groupSize: val("groupSize"), type: val("type"),
  };
  for (const k of ["company", "date", "message"] as const) if (val(k)) body[k] = val(k);

  sending = true;
  submitBtn.disabled = true;
  submitBtn.setAttribute("aria-busy", "true");
  submitLabel.textContent = "Sending";
  try {
    const res = await post(body);
    const data = await res.json().catch(() => null);
    if (res.ok && data?.inquiry) {
      success(data.inquiry.ref ?? data.inquiry.id ?? "", body.email);
      return;
    }
    const err = data?.error;
    if (err?.fields) {
      let firstBad: HTMLElement | null = null;
      for (const [k, m] of Object.entries(err.fields as Record<string, string>)) {
        if ((FIELDS as string[]).includes(k)) {
          show(k as Field, String(m));
          firstBad ??= form.querySelector<HTMLElement>(`.chips[data-name="${k}"] .chip, [name="${k}"]:not([type="hidden"])`);
        }
      }
      setStatus("Not sent. Check the highlighted fields.");
      firstBad?.focus();
    } else if (res.status === 429) {
      setStatus("Too many tries in a short time. Wait a few minutes, then try again.", true);
    } else if (res.status === 404) {
      setStatus("Inquiries are not open yet. Try again shortly, or message us on WhatsApp.", true);
    } else {
      setStatus(err?.message ? `Not sent. ${err.message}` : "Not sent. Something went wrong on our side.", true);
    }
  } catch {
    setStatus("Not sent. Check your connection and try again, or message us on WhatsApp.", true);
  } finally {
    sending = false;
    submitBtn.disabled = false;
    submitBtn.removeAttribute("aria-busy");
    submitLabel.textContent = "Send inquiry";
  }
});

function success(ref: string, email: string) {
  const refEl = document.getElementById("done-ref")!;
  refEl.textContent = ref;
  if (MOTION) {
    // the reference resolves character by character, like a scoreboard settling
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    const o = { p: 0 };
    gsap.to(o, { p: 1, duration: 1.1, delay: 0.25, ease: "power1.out", onUpdate: () => {
      const fixed = Math.floor(o.p * ref.length);
      refEl.textContent = ref.split("").map((c, i) => (i < fixed || c === "-" ? c : chars[(Math.random() * chars.length) | 0])).join("");
    }, onComplete: () => { refEl.textContent = ref; } });
  }
  document.getElementById("done-p")!.textContent = `We will reply to ${email} with a plan and a price.`;
  form.hidden = true;
  done.hidden = false;
  done.focus({ preventScroll: true });
  const circle = done.querySelector(".done-circle")!;
  const dotEl = done.querySelector(".done-dot")!;
  if (MOTION) {
    gsap.fromTo(circle, { strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: 1.2, ease: "power2.inOut" });
    gsap.fromTo(dotEl, { opacity: 0, y: -10 }, { opacity: 1, y: 0, duration: 0.3, ease: "power3.out", delay: 1.15 });
    gsap.from(done.querySelectorAll(".done-in, .done-h, .done-p, #done-again"), { opacity: 0, y: 12, duration: 0.5, stagger: 0.08, delay: 0.3, ease: "power3.out" });
  } else {
    gsap.set(circle, { strokeDashoffset: 0 });
    gsap.set(dotEl, { opacity: 1 });
  }
  const top = done.getBoundingClientRect().top + scrollY - 120;
  if (top < scrollY) scrollTo({ top, behavior: MOTION ? "smooth" : "auto" });
}

document.getElementById("done-again")!.addEventListener("click", () => {
  form.reset();
  resetChips();
  touched.clear();
  FIELDS.forEach((f) => show(f, ""));
  done.hidden = true;
  form.hidden = false;
  form.querySelector<HTMLInputElement>("#f-name")!.focus();
});
